import type React from 'react';
import { useMemo } from 'react';

import { Link, type LinkProps } from '@tanstack/react-router';

import { cn } from '@/lib/utils';

import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

export type SidebarLinkProps = Omit<LinkProps, 'children'> & {
  icon?: React.ReactNode;
  label: string;
  className?: string;
  isSidebarCollapsed?: boolean;
  activeOptions?: LinkProps['activeOptions'];
  onClick?: () => void;
};

export const SidebarLink = ({
  icon,
  label,
  className,
  isSidebarCollapsed,
  activeOptions,
  ...props
}: SidebarLinkProps) => {
  const link = useMemo(
    () => (
      <Link
        className={cn(
          'flex min-h-[41px] items-center gap-[12px] rounded-md px-6 py-2 text-md leading-tight text-primary-darker hover:bg-primary-darker/10 max-md:px-2 [&_svg]:size-6 [&_svg]:shrink-0 [&_svg]:text-primary',
          isSidebarCollapsed && 'justify-center px-2'
        )}
        activeProps={{
          className: cn(
            'bg-primary-dark !text-primary-lighter hover:!bg-primary-dark/90 [&_svg]:!text-primary-lighter',
            className
          ),
        }}
        activeOptions={{
          exact: true,
          includeSearch: false,
          ...activeOptions,
        }}
        {...props}
      >
        {icon ? icon : null}
        {isSidebarCollapsed ? null : label}
      </Link>
    ),
    // biome-ignore lint/correctness/useExhaustiveDependencies: ignored
    [activeOptions, isSidebarCollapsed, icon, label, className, props]
  );

  return isSidebarCollapsed ? (
    <Tooltip>
      <TooltipTrigger>{link}</TooltipTrigger>
      <TooltipContent
        side="right"
        className="bg-primary-dark py-1 text-base text-primary-lighter shadow [&_polygon]:fill-primary-dark [&_svg]:bg-primary-dark"
      >
        {label}
      </TooltipContent>
    </Tooltip>
  ) : (
    link
  );
};
